import React, { useEffect, useRef, useState } from 'react';
import Styles from './ButtonStyles.module.scss';
import { MyPolicyCallByAction } from '../../../services/LoginServiceV2';
import RichText from '../../richtext';
import { replacePlaceholder } from '../../../utils/utils';
import ReactInputSelect from '../../ReactInputSelect/ReactInputSelect';
import {
  PW_CHANGED,
  LOGIN_MFA,
  DEPRICATING_SMS,
  DEPRICATING_EMAIL,
  DEPRICATED_EMAIL,
  OTP_NR_LIST,
  OTP_NR_ADD,
  OTP_NR_CHANGE,
  OTP_NR_CONFIRM,
  OTP_CONTINUE
} from '../../../utils/constants';
import { Spinner } from 'react-bootstrap';
import { DepricatingChannelSchema } from '../Login.types';
import mainStyles from '../ReusableLogin.module.scss';

const DepricatingChannels: React.FC<DepricatingChannelSchema> = ({
  useCase,
  mainContent,
  changeCurrentView,
  response,
  resetPwdSuccessContent,
  loginIcons,
  errorContent
}) => {
  const [content, setContent] = useState<Record<string, any>>({});
  const [options, setOptions] = useState<Record<string, string>[]>([]);
  const [selectedOption, setSelectedOption] = useState<Record<string, any>>({});
  const [primaryButtonValue, setPrimaryButtonValue] = useState('');
  const [secondaryButtonValue, setSecondaryButtonValue] = useState('');
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [techError, setTechError] = useState(false);
  const controllerRef = useRef<AbortController | null>(null);

  const handleClick = (type: string) => {
    if (showLoader) {
      controllerRef?.current?.abort();
    }
    setShowLoader(true);
    setTechError(false);
    if (useCase === PW_CHANGED) {
      changeCurrentView('', { status: LOGIN_MFA });
      setShowLoader(false);
    } else if (type) {
      MyPolicyCallByAction(type, selectedOption?.value, controllerRef)
        .then((res: any) => {
          if (res?.status) {
            if (res?.status !== 'NOK' && res?.status !== 'ERROR') {
              changeCurrentView('', res);
            } else {
              setTechError(true);
            }
          } else {
            setTechError(true);
          }
          setShowLoader(false);
        })
        .catch((err) => {
          setShowLoader(false);
          setTechError(true);
          console.log(err);
        });
    }
  };

  //To handle a number from dropdown of multiple numbers
  const handleSelection = (event: any) => {
    setSelectedOption({ value: event?.value?.value ?? '', name: event?.value?.name ?? '' });
  };

  //Returns the dynamic values which needs to be replaced in desc based on resp
  const getDynamicValues = (): string[] => {
    if (useCase === DEPRICATING_EMAIL) {
      if (response?.msisdn) {
        return [response?.recipient, response?.msisdn];
      } else {
        return [response?.date];
      }
    } else if (useCase === DEPRICATING_SMS) {
      return [response?.recipient];
    }
    return [];
  };

  //To pickup content based on the response code
  useEffect(() => {
    if (mainContent) {
      if (useCase === DEPRICATING_SMS && mainContent?.depSmsSection?.length > 0) {
        setContent(mainContent?.depSmsSection?.[0]);
      }
      if (useCase === DEPRICATING_EMAIL) {
        if (response?.msisdn && mainContent?.depEmailSection?.length > 0) {
          setContent(mainContent?.depEmailSection?.[0]);
        } else {
          setContent(mainContent?.depEmailSectionV2?.[0]);
        }
      }
      if (useCase === DEPRICATED_EMAIL && mainContent?.emailDeprecatedSection?.length > 0) {
        setContent(mainContent?.emailDeprecatedSection?.[0]);
      }
      if (useCase === OTP_NR_LIST && mainContent?.otpNumListSection?.length > 0) {
        setContent(mainContent?.otpNumListSection?.[0]);
      }
      if (useCase === PW_CHANGED && resetPwdSuccessContent) {
        setContent(resetPwdSuccessContent);
      }
    }
  }, [useCase, mainContent, response]);

  //assigning codes for specific buttons based on response
  useEffect(() => {
    if (useCase) {
      switch (useCase) {
        case DEPRICATING_SMS:
          setSecondaryButtonValue(OTP_NR_CHANGE);
          setPrimaryButtonValue(OTP_NR_CONFIRM);
          break;

        case DEPRICATING_EMAIL:
          if (response?.msisdn) {
            setSecondaryButtonValue(OTP_NR_CHANGE);
            setPrimaryButtonValue(OTP_NR_CONFIRM);
          } else {
            setSecondaryButtonValue(OTP_NR_ADD);
            setPrimaryButtonValue(OTP_CONTINUE);
          }
          break;

        case DEPRICATED_EMAIL:
          setPrimaryButtonValue(OTP_NR_ADD);
          break;

        case OTP_NR_LIST:
          setSecondaryButtonValue(OTP_NR_ADD);
          setPrimaryButtonValue(OTP_NR_LIST);
          break;

        case PW_CHANGED:
          setPrimaryButtonValue(PW_CHANGED);
          break;

        default:
          break;
      }
    }
    if (
      response &&
      response?.recipients &&
      Object?.keys(response?.recipients)?.length > 0 &&
      useCase === OTP_NR_LIST
    ) {
      const optionsList = (Object?.values(response?.recipients) || [])?.map(
        (number: any, index: number) => {
          return {
            value: `${index + 1}`,
            name: index === 0 ? `${number ?? ''} ${content?.currentLbl ?? ''}` : number ?? ''
          };
        }
      );
      if (optionsList?.length > 0) {
        setOptions(optionsList);
        setSelectedOption(optionsList?.[0]);
      }
    }
  }, [response, content]);

  return (
    <div>
      {useCase === PW_CHANGED && (
        <img
          src={loginIcons?.checkmark?.filename ?? ''}
          alt='chekcmark'
          className='mb-4 d-flex mx-auto'
          width={72}
          height={72}
        />
      )}
      {content?.title && <h2 className='text-center my-4 p-0'>{content?.title}</h2>}
      {content?.description && (
        <div className={`${useCase === PW_CHANGED ? 'text-center' : ''}`}>
          <RichText
            content={JSON.parse(
              replacePlaceholder(JSON.stringify(content?.description), getDynamicValues())
            )}
          />
        </div>
      )}
      {options?.length > 0 && useCase === OTP_NR_LIST && (
        <div className='my-4'>
          <ReactInputSelect
            name='phone number'
            content={content}
            options={options}
            optionsValue='value'
            defaultValue={selectedOption}
            optionsLabel='name'
            autoSelectValueFromOptions={false}
            onChange={handleSelection}
            disableValues={['empty']}
            loginIcons={loginIcons}
          />
          {techError && errorContent?.technicalError && (
            <p className={mainStyles.credentialerror}>
              <img
                src={loginIcons?.alertTriangle?.filename}
                alt='alert'
                className={mainStyles.warningicon}
              />
              {errorContent?.technicalError}
            </p>
          )}
        </div>
      )}
      {(content?.primaryBtnLbl || content?.secondaryBtnLbl) && (
        <div className='mt-4'>
          {showLoader && (
            <Spinner
              as='span'
              animation='border'
              size='sm'
              role='status'
              aria-hidden='true'
              className='d-block mx-auto mb-3'
            />
          )}
          {techError && errorContent?.technicalError && (
            <p
              className={`${mainStyles.credentialerror} mb-2 mt-0 position-static h-auto justify-content-center`}
            >
              <img
                src={loginIcons?.alertTriangle?.filename}
                alt='alert'
                className={mainStyles.warningicon}
              />
              {errorContent?.technicalError}
            </p>
          )}
          <div className='d-flex justify-content-center gap-4'>
            {content?.secondaryBtnLbl && useCase !== DEPRICATED_EMAIL && (
              <button
                className={Styles.changeButton}
                onClick={() => handleClick(secondaryButtonValue)}
                disabled={showLoader}
              >
                {content?.secondaryBtnLbl}
              </button>
            )}
            {content?.primaryBtnLbl && (
              <button
                className={Styles.continueButton}
                onClick={() => handleClick(primaryButtonValue)}
                disabled={showLoader}
              >
                {content?.primaryBtnLbl}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DepricatingChannels;
